import api from "../axios";
import ai from "../axios/ai";
import { formatISODate } from "../../utils/helper";

const getAccountId = () => {
  return sessionStorage.getItem("acc_id");
};

const getPatientId = () => sessionStorage.getItem("_pt_id");

export async function exportPatientReadings(
  patientId,
  format = "pdf",
  readingType = null,
  timeFrame = null,
  dateRange = null,
  unreviewed = false
) {
  const accountId = getAccountId();
  let url = `/rpm/${accountId}/export-readings/${patientId}/${format}/`;
  let queryParams = [];

  // Add unreviewed filter if requested
  if (unreviewed) {
    queryParams.push("unreviewed_unescalated=true");
  }

  // Add time frame filter only if NOT getting unreviewed readings
  // For unreviewed, we want ALL unreviewed readings regardless of time frame
  if (timeFrame && !unreviewed) {
    queryParams.push(`filter=${timeFrame}`);
  }

  // Add date range if provided
  if (dateRange && dateRange.startDate && dateRange.endDate) {
    queryParams.push(
      `start_date=${formatISODate(
        dateRange.startDate
      )}&end_date=${formatISODate(dateRange.endDate)}`
    );
  }

  // Add reading type if provided (skip for unreviewed "all types" exports)
  if (readingType && !unreviewed) {
    queryParams.push(`reading_type=${readingType}`);
  }

  // Append query parameters if any
  if (queryParams.length > 0) {
    url += `?${queryParams.join("&")}`;
  }

  // For file downloads, we need to set responseType to 'blob'
  const response = await api.get(url, {
    responseType: "blob",
    headers: {
      "Content-Type": format === "pdf" ? "application/pdf" : "image/png",
    },
  });

  return response.data;
}

export async function shareReadings(
  patientId,
  format,
  readingType,
  timeFrame,
  dateRange,
  email,
  unreviewed = false
) {
  const accountId = getAccountId();
  let url = `/rpm/${accountId}/share-export-readings/${patientId}/${format}/`;

  // Create form data object
  const formData = new FormData();

  // Add unreviewed filter if requested
  if (unreviewed) {
    formData.append("unreviewed_unescalated", "true");
  }

  // Add reading type only if not unreviewed (for unreviewed, get all types)
  if (readingType && !unreviewed) {
    formData.append("type", readingType);
  }

  // Add time frame only if NOT getting unreviewed readings
  // For unreviewed, we want ALL unreviewed readings regardless of time frame
  if (timeFrame && !unreviewed) {
    formData.append("filter", timeFrame);
  }

  if (dateRange.startDate && dateRange.endDate) {
    formData.append("start_date", formatISODate(dateRange.startDate));
    formData.append("end_date", formatISODate(dateRange.endDate));
  }

  if (Array.isArray(email)) {
    email.forEach((_email) => formData.append("email", _email));
  } else {
    formData.append("email", email);
  }

  const response = await api.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    params: {
      ...(unreviewed && { unreviewed_unescalated: "true" }),
      // ...(dateRange?.startDate && {
      //   start_date: formatISODate(dateRange.startDate),
      // }),
      // ...(dateRange?.endDate && {
      //   end_date: formatISODate(dateRange.endDate),
      // }),
    },
  });
  return response.data;
}

export async function shareAuditReport(
  file,
  email,
  patientId,
  date,
  subject_name
) {
  const tenantId = getAccountId();
  const formData = new FormData();

  let normalizedFile = file;
  if (file instanceof Blob && file.type !== "application/pdf") {
    normalizedFile = new Blob([file], { type: "application/pdf" });
  }

  const fallbackName = "report.pdf";
  const fileName = subject_name ? `${subject_name}.pdf` : (file?.name || fallbackName);

  if (
    typeof File !== "undefined" &&
    normalizedFile instanceof Blob &&
    !(normalizedFile instanceof File)
  ) {
    normalizedFile = new File([normalizedFile], fileName, {
      type: normalizedFile.type || "application/pdf",
    });
  }

  formData.append("pdf", normalizedFile, fileName);
  formData.append("email", email);
  formData.append("date", date);
  if (patientId) {
    formData.append("patient_id", patientId);
  } else {
    formData.append("subject_name", subject_name);
  }

  const response = await api.post(
    `/billing/patient-billing/${tenantId}/email-audit-report/`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
}

export async function uploadPatientDocuments(formData) {
  const tenantId = getAccountId();
  const patientId = getPatientId();

  // append patient id
  formData.append("patient_id", patientId);

  const response = await api.post(
    `/patients/${tenantId}/documents/`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
}

export async function uploadPatientDocumentsWithId(formData, patientId) {
  const tenantId = getAccountId();

  // append patient id
  formData.append("patient_id", patientId);

  const response = await api.post(
    `/patients/${tenantId}/documents/`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
}

export async function getPatientDocuments(documentType, lastNMonths) {
  const tenantId = getAccountId();
  const patientId = getPatientId();

  const response = await api.get(`/patients/${tenantId}/documents/`, {
    params: {
      ...(documentType && { document_type: documentType }),
      ...(lastNMonths && { last_n_months: lastNMonths }),

      patient_id: patientId,
    },
  });
  return response.data;
}

export async function getPatientDocumentsById(patientId, documentType) {
  const tenantId = getAccountId();

  const response = await api.get(`/patients/${tenantId}/documents/`, {
    params: {
      ...(documentType && { document_type: documentType }),
      patient_id: patientId,
    },
  });
  return response.data;
}

export async function deletePatientDocument(id) {
  const tenantId = getAccountId();
  const patientId = getPatientId();

  const response = await api.delete(`/patients/${tenantId}/documents/${id}/`);
  return response.data;
}

export async function duplicatePatientDocument(id, formData) {
  const tenantId = getAccountId();
  const patientId = getPatientId();

  // append patient id
  formData.append("patient_id", patientId);

  const response = await api.post(
    `/patients/${tenantId}/documents/${id}/duplicate/`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
}

export async function replacePatientDocument(id, formData) {
  const tenantId = getAccountId();
  const patientId = getPatientId();

  // append patient id
  formData.append("patient_id", patientId);

  const response = await api.post(
    `/patients/${tenantId}/documents/${id}/replace/`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
}

export async function uploadChatbotContext(body) {
  const response = await ai.post("/chatbot/process-s3-reports/", body);
  return response.data;
}
